import { de, enUS, nl, pt, tr, fr, es, it } from 'date-fns/locale';

type LocaleCode =
  | 'tr_TR'
  | 'en_NL'
  | 'nl_NL'
  | 'de_DE'
  | 'es_ES'
  | 'fr_FR'
  | 'it_IT'
  | 'pt_PT'
  | 'en_US';
export type CountryCode =
  | 'TR'
  | 'NL'
  | 'BE'
  | 'GB'
  | 'DE'
  | 'FR'
  | 'US'
  | 'ES'
  | 'IT'
  | 'PT';
type LanguageCode = 'tr' | 'nl' | 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt';
type Currency = 'EUR' | 'USD' | 'GBP' | 'TRY';

type CountryName = {
  TR: 'Turkiye';
  NL: 'Netherlands';
  BE: 'Belgium';
  GB: 'United Kingdom';
  DE: 'Germany';
  FR: 'France';
  US: 'United States';
  ES: 'Spain';
  IT: 'Italy';
  PT: 'Portugal';
}[CountryCode];

type LanguageName = {
  tr: 'Turkish';
  nl: 'Nederlands';
  en: 'English';
  de: 'German';
  fr: 'French';
  es: 'Spanish';
  it: 'Italian';
  pt: 'Portuguese';
}[LanguageCode];

interface ILocale {
  currency: Currency;
  code: string;
  title: string;
  language: LanguageCode;
  country: CountryCode;
  languageId: number;
  countryId: number;
  countryName: CountryName;
  order: number;
  languageNameTranslation?: string;
}

interface ICountry {
  country: CountryCode;
  countryName: CountryName;
  countryId: number;
  currency: Currency;
}

interface ILanguage {
  language: LanguageCode;
  title: LanguageName;
  languageId: number;
  translationName: string;
  mainLocale: string;
}

type ICountries = Record<
  'TR' | 'NL' | 'US' | 'BE' | 'GB' | 'DE' | 'FR' | 'ES' | 'IT' | 'PT',
  ICountry
>;
type ILocales = Record<LocaleCode, ILocale>;

export const COUNTRIES: ICountries = {
  NL: {
    country: 'NL',
    countryName: 'Netherlands',
    countryId: 31,
    currency: 'EUR',
  },
  TR: {
    country: 'TR',
    countryName: 'Turkiye',
    countryId: 90,
    currency: 'TRY',
  },
  BE: {
    country: 'BE',
    countryName: 'Belgium',
    countryId: 56,
    currency: 'EUR',
  },
  GB: {
    country: 'GB',
    countryName: 'United Kingdom',
    countryId: 826,
    currency: 'GBP',
  },
  DE: {
    country: 'DE',
    countryName: 'Germany',
    countryId: 276,
    currency: 'EUR',
  },
  FR: {
    country: 'FR',
    countryName: 'France',
    countryId: 250,
    currency: 'EUR',
  },
  US: {
    country: 'US',
    countryName: 'United States',
    countryId: 840,
    currency: 'USD',
  },
  ES: {
    country: 'ES',
    countryName: 'Spain',
    countryId: 724,
    currency: 'EUR',
  },
  IT: {
    country: 'IT',
    countryName: 'Italy',
    countryId: 380,
    currency: 'EUR',
  },
  PT: {
    country: 'PT',
    countryName: 'Portugal',
    countryId: 620,
    currency: 'EUR',
  },
};

export const LANGUAGES = {
  TR: {
    languageId: 1,
    language: 'tr',
    title: 'Turkish',
    translationName: 't-tr',
    mainLocale: 'tr-TR',
  },
  EN: {
    languageId: 2,
    language: 'en',
    title: 'English',
    translationName: 't-en',
    mainLocale: 'en-GB',
  },
  NL: {
    languageId: 3,
    language: 'nl',
    title: 'Dutch',
    translationName: 't-nl',
    mainLocale: 'nl-NL',
  },
  DE: {
    languageId: 4,
    language: 'de',
    title: 'German',
    translationName: 't-de',
    mainLocale: 'de-DE',
  },
  FR: {
    languageId: 5,
    language: 'fr',
    title: 'French',
    translationName: 't-fr',
    mainLocale: 'fr-FR',
  },
  ES: {
    languageId: 6,
    language: 'es',
    title: 'Spanish',
    translationName: 't-es',
    mainLocale: 'es-ES',
  },
  IT: {
    languageId: 7,
    language: 'it',
    title: 'Italian',
    translationName: 't-it',
    mainLocale: 'it-IT',
  },
  PT: {
    languageId: 8,
    language: 'pt',
    title: 'Portuguese',
    translationName: 't-pt',
    mainLocale: 'pt-PT',
  },
} as const;

const LOCALES: ILocales = {
  tr_TR: {
    code: 'tr-TR',
    title: 'Turkce - Turkiye',
    language: LANGUAGES.TR.language,
    languageId: LANGUAGES.TR.languageId,
    country: COUNTRIES.TR.country,
    countryName: COUNTRIES.TR.countryName,
    countryId: COUNTRIES.TR.countryId,
    order: 1,
    languageNameTranslation: LANGUAGES.TR.translationName,
    currency: 'TRY',
  },
  en_NL: {
    code: 'en-NL',
    language: LANGUAGES.EN.language,
    languageId: LANGUAGES.EN.languageId,
    title: 'English - Netherlands',
    country: COUNTRIES.NL.country,
    countryName: COUNTRIES.NL.countryName,
    countryId: COUNTRIES.NL.countryId,
    order: 4,
    languageNameTranslation: LANGUAGES.EN.translationName,
    currency: 'EUR',
  },
  nl_NL: {
    code: 'nl-NL',
    language: LANGUAGES.NL.language,
    languageId: LANGUAGES.NL.languageId,
    title: 'Dutch - Netherlands',
    country: COUNTRIES.NL.country,
    countryName: COUNTRIES.NL.countryName,
    countryId: COUNTRIES.NL.countryId,
    order: 4,
    languageNameTranslation: LANGUAGES.EN.translationName,
    currency: 'EUR',
  },
  de_DE: {
    code: 'de-DE',
    language: LANGUAGES.DE.language,
    languageId: LANGUAGES.DE.languageId,
    title: 'Deutsch - Deutschland',
    country: COUNTRIES.DE.country,
    countryName: COUNTRIES.DE.countryName,
    countryId: COUNTRIES.DE.countryId,
    order: 5,
    languageNameTranslation: LANGUAGES.DE.translationName,
    currency: 'EUR',
  },
  es_ES: {
    code: 'es-ES',
    language: LANGUAGES.ES.language,
    languageId: LANGUAGES.ES.languageId,
    title: 'Español - España',
    country: COUNTRIES.ES.country,
    countryName: COUNTRIES.ES.countryName,
    countryId: COUNTRIES.ES.countryId,
    order: 6,
    languageNameTranslation: LANGUAGES.ES.translationName,
    currency: 'EUR',
  },
  fr_FR: {
    code: 'fr-FR',
    language: LANGUAGES.FR.language,
    languageId: LANGUAGES.FR.languageId,
    title: 'Français - France',
    country: COUNTRIES.FR.country,
    countryName: COUNTRIES.FR.countryName,
    countryId: COUNTRIES.FR.countryId,
    order: 7,
    languageNameTranslation: LANGUAGES.FR.translationName,
    currency: 'EUR',
  },
  it_IT: {
    code: 'it-IT',
    language: LANGUAGES.IT.language,
    languageId: LANGUAGES.IT.languageId,
    title: 'Italiano - Italia',
    country: COUNTRIES.IT.country,
    countryName: COUNTRIES.IT.countryName,
    countryId: COUNTRIES.IT.countryId,
    order: 8,
    languageNameTranslation: LANGUAGES.IT.translationName,
    currency: 'EUR',
  },
  pt_PT: {
    code: 'pt-PT',
    language: LANGUAGES.PT.language,
    languageId: LANGUAGES.PT.languageId,
    title: 'Português - Portugal',
    country: COUNTRIES.PT.country,
    countryName: COUNTRIES.PT.countryName,
    countryId: COUNTRIES.PT.countryId,
    order: 9,
    languageNameTranslation: LANGUAGES.PT.translationName,
    currency: 'EUR',
  },
  en_US: {
    code: 'en-US',
    language: LANGUAGES.EN.language,
    languageId: LANGUAGES.EN.languageId,
    title: 'English - United States',
    country: COUNTRIES.US.country,
    countryName: COUNTRIES.US.countryName,
    countryId: COUNTRIES.US.countryId,
    order: 10,
    languageNameTranslation: LANGUAGES.EN.translationName,
    currency: 'USD',
  },
};

export const dateLocales = {
  en: enUS,
  tr: tr,
  nl: nl,
  de: de,
  es: es,
  fr: fr,
  it: it,
  pt: pt,
};

export const getCurrencyByCountryCode = (countryCode: CountryCode) => {
  return COUNTRIES[countryCode]?.currency || null;
};

export const DEFAULT_LOCALE = LOCALES.en_NL.code;

export type { ILocale, ILocales, ICountry, ILanguage };
export default LOCALES;
